import { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { API_URL, api } from '../../services/api';

interface TissuDetails {
  id: number;
  nom: string;
  type: string;
  couleur: string;
  quantite_stock: number;
  unite: string;
  image_url: string;
  boutique_id: number | null;
}

export default function FicheTissuPublic() {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [tissu, setTissu] = useState<TissuDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<any | null>(null);

  useEffect(() => {
    // Vérifier si un utilisateur gérant/vendeur est connecté localement
    const storedUser = localStorage.getItem('atelier_bassoum_user');
    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        console.error("Error parsing user info", e);
      }
    }

    if (!id || !token) {
      setError("Paramètres d'accès incorrects ou jeton de sécurité manquant.");
      setLoading(false);
      return;
    }

    const fetchTissu = async () => {
      try {
        const res = await fetch(`${API_URL}/tissus/public-view/${id}?token=${token}`);
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          throw new Error(data.message || `Erreur serveur (${res.status})`);
        }
        const data = await res.json();
        setTissu(data);
      } catch (err: any) {
        setError(err.message || "Impossible de charger les détails du tissu.");
      } finally {
        setLoading(false);
      }
    };

    fetchTissu();
  }, [id, token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-terre-500"></div>
        <p className="mt-4 text-sm font-semibold text-gray-500">Lecture du QR Code du tissu...</p>
      </div>
    );
  }

  if (error || !tissu) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-6 text-center">
        <div className="w-16 h-16 bg-red-100 text-red-600 rounded-full flex items-center justify-center text-2xl mb-4">
          <i className="ri-error-warning-line" />
        </div>
        <h2 className="font-playfair font-bold text-lg text-gray-900">Code Non Reconnu</h2>
        <p className="mt-2 text-sm text-gray-500 max-w-sm">{error || "Ce tissu n'existe pas ou le QR code est invalide."}</p>
      </div>
    );
  }

  const handleGoToVente = () => {
    // Stocker temporairement l'info pour l'ajouter à la vente à l'ouverture de la page de vente
    localStorage.setItem('atelier_bassoum_quick_sell_tissu_id', String(tissu.id));
    navigate('/vente-tissu');
  };

  return (
    <div className="min-h-screen bg-gray-50 font-nunito pb-12">
      {/* Header */}
      <header className="bg-white border-b border-gray-100 px-6 py-4 sticky top-0 z-10 shadow-sm flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-terre-500 flex items-center justify-center text-white text-lg font-bold shadow-sm shadow-terre-500/20">
            B
          </div>
          <div>
            <h1 className="font-playfair font-bold text-base text-gray-900">Atelier Bassoum</h1>
            <p className="text-[10px] text-gray-400 font-bold uppercase tracking-wider">Consultation Stock Boutique</p>
          </div>
        </div>
        <span className="px-2.5 py-0.5 bg-terre-50 text-terre-700 text-[10px] font-black rounded-full border border-terre-100 uppercase tracking-widest">
          Stock
        </span>
      </header>

      <div className="max-w-md mx-auto p-4 md:p-6 space-y-6 mt-4">
        {/* Main Card */}
        <div className="bg-white rounded-3xl border border-gray-100 overflow-hidden shadow-xl shadow-gray-200/50">
          {/* Image */}
          {tissu.image_url ? (
            <div className="aspect-video w-full overflow-hidden border-b border-gray-100 bg-gray-50 relative">
              <img 
                src={api.getProxyUrl(tissu.image_url)} 
                alt={tissu.nom} 
                className="w-full h-full object-cover" 
              />
              <div className="absolute top-4 right-4 px-3 py-1 bg-black/40 backdrop-blur-md text-white text-xs font-bold rounded-full">
                ID : {tissu.id}
              </div>
            </div>
          ) : (
            <div className="aspect-video w-full bg-terre-50/30 border-b border-gray-100 flex flex-col items-center justify-center text-terre-600/60 gap-1.5">
              <i className="ri-palette-line text-4xl" />
              <span className="text-xs font-black uppercase tracking-wider">Pas de photo</span>
            </div>
          )}

          {/* Details */}
          <div className="p-6 space-y-6">
            <div>
              <h2 className="font-playfair font-bold text-2xl text-gray-900 leading-tight">{tissu.nom}</h2>
              <div className="flex gap-2 mt-2">
                {tissu.type && (
                  <span className="px-2.5 py-1 bg-gray-100 text-gray-600 text-[10px] font-bold rounded-md uppercase">
                    {tissu.type}
                  </span>
                )}
                {tissu.couleur && (
                  <span className="px-2.5 py-1 bg-gray-100 text-gray-600 text-[10px] font-bold rounded-md uppercase">
                    {tissu.couleur}
                  </span>
                )}
                <span className="px-2.5 py-1 bg-terre-50 text-terre-600 text-[10px] font-bold rounded-md uppercase">
                  Boutique {tissu.boutique_id || 1}
                </span>
              </div>
            </div>

            {/* Stock Quantity - BIG DISPLAY */}
            <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100 flex flex-col items-center text-center">
              <span className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Quantité restante en boutique</span>
              <span className="text-3xl font-black text-gray-900">
                {tissu.quantite_stock} <span className="text-lg font-bold text-gray-500">{tissu.unite || 'mètres'}</span>
              </span>
              {tissu.quantite_stock <= 5 && (
                <span className="mt-2 text-[10px] font-bold text-red-600 bg-red-50 border border-red-100 px-2 py-0.5 rounded-full uppercase tracking-wider flex items-center gap-1 animate-pulse">
                  <i className="ri-error-warning-fill" /> Stock Bas
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Manager Mode Panel (Only if logged in) */}
        {user ? (
          <div className="bg-foret-50 border border-foret-100 rounded-3xl p-5 space-y-4 shadow-md animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-foret-500 text-white flex items-center justify-center text-sm shadow-sm">
                <i className="ri-user-settings-line" />
              </div>
              <div>
                <p className="text-xs text-foret-600">Mode Gérant connecté</p>
                <p className="text-sm font-bold text-gray-800">{user.nom} ({user.role})</p>
              </div>
            </div>

            <div className="h-px bg-foret-100" />

            <div className="grid grid-cols-2 gap-3">
              <button 
                onClick={handleGoToVente}
                className="px-4 py-3 bg-foret-500 hover:bg-foret-600 text-white font-bold text-xs rounded-xl shadow-md transition-all cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-shopping-cart-2-line" /> Vendre ce tissu
              </button>
              <button 
                onClick={() => navigate('/stocks')}
                className="px-4 py-3 bg-white border border-gray-200 text-gray-700 hover:bg-gray-50 font-bold text-xs rounded-xl shadow-sm transition-all cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-edit-box-line" /> Gérer le stock
              </button>
            </div>
          </div>
        ) : (
          <div className="text-center">
            <button 
              onClick={() => navigate('/login')}
              className="text-xs text-gray-400 hover:text-gray-600 underline font-bold cursor-pointer"
            >
              Connexion Compte Gérant
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
